/**
 * Preview toolbar for the snapshot editor.
 *
 * Deliberately a fraction of the version editor's toolbar: there is no recorder,
 * no autoplay and no plan counters here.
 *
 * The two right-hand buttons are the editor's two ways out of it: Export builds
 * a file to download, Share link puts the same playable on demo.playin.com.tr
 * and hands back its link. That is the version editors' preview site, but not
 * their flow — no plan test is spent and no preview record is written — and it
 * is not the delivery dialog's folder-wide Preview either. See
 * controller/snapshotEditor/previewSnapshot.js.
 */

import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress, IconButton, Tooltip, Typography } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import GetAppIcon from "@material-ui/icons/GetApp";
import LinkIcon from "@material-ui/icons/Link";
import PreviewDeviceControls from "../previewDeviceControls";
import { FIELD_LABEL } from "./panelPalette";
import {
  bestPlayableBytes,
  formatBytes,
  measuredPlayableBytes,
} from "../../modules/playableSizeEstimate";

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
    minHeight: "40px",
  },
  titleSlot: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    marginRight: "8px",
  },
  title: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "13px",
    lineHeight: 1.3,
    opacity: 0.85,
  },
  size: {
    fontSize: "11px",
    lineHeight: 1.3,
    color: FIELD_LABEL,
    whiteSpace: "nowrap",
  },
  // On the button, not beside it: a mark that shifted the row would move every
  // control under the cursor the moment an export started.
  exportSlot: { position: "relative", display: "inline-flex" },
  exportSpinner: { position: "absolute", right: 0, top: 0, color: "#8ab4c8" },
  exportDot: {
    position: "absolute",
    right: 0,
    top: "1px",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
  },
  exportDotSuccess: { backgroundColor: "#7cc47f" },
  exportDotWarning: { backgroundColor: "#d8a657" },
  exportDotError: { backgroundColor: "#e57373" },
});

export default function ToolbarSnapshotPreview({
  editor,
  isPortrait,
  devices,
  deviceId,
  isCustomSize,
  previewWidth,
  previewHeight,
  zoom,
  zoomSteps,
  effectiveZoom,
  onSelectZoom,
  onToggleOrientation,
  onSelectDevice,
  onCustomSize,
  isMuted,
  onToggleMute,
  onReload,
  onExport,
  // The running or just-finished export, or null.
  exportJob,
  onShareLink,
}) {
  const classes = useStyles();
  // The last build of this snapshot, if it was built from the bytes on disk
  // now; otherwise the upper bound the snapshot file gives.
  const summary = (editor.snapshots || []).find(
    (item) => item.fileName === editor.selectedFileName
  );
  const measuredBytes = measuredPlayableBytes(summary, "applovin", "en");
  // Measured beats estimated, and the server's estimate — which ran the
  // engine's asset rules over this very snapshot — beats the rule of thumb
  // that only knows how big the file is.
  const sizeBytes = bestPlayableBytes({
    summary,
    network: "applovin",
    language: "en",
    serverEstimate: editor.snapshotEstimatedBytes,
    snapshotBytes: editor.snapshotSizeBytes,
  });

  return (
    <div className={classes.root}>
      <div className={classes.titleSlot}>
        <Typography className={classes.title} title={editor.selectedFileName || ""}>
          {editor.selectedFileName || ""}
        </Typography>
        {sizeBytes && (
          <Tooltip
            title={
              measuredBytes
                ? "Size of the html the last build produced, for this snapshot as it is on disk."
                : "Estimated size of the exported playable, from the assets the export would keep plus the engine. Fitted so it never reads under the real file, and at most 80 KB over. Build it once and this shows the measured size."
            }
          >
            <span className={classes.size}>
              {measuredBytes ? "" : "~"}
              {formatBytes(sizeBytes)}
            </span>
          </Tooltip>
        )}
      </div>

      <PreviewDeviceControls
        isPortrait={isPortrait}
        devices={devices}
        deviceId={deviceId}
        isCustomSize={isCustomSize}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
        zoom={zoom}
        zoomSteps={zoomSteps}
        effectiveZoom={effectiveZoom}
        onSelectZoom={onSelectZoom}
        onSelectDevice={onSelectDevice}
        onCustomSize={onCustomSize}
        onToggleOrientation={onToggleOrientation}
      />

      {/* Silences the preview only — the snapshot's own master volume is not
          touched, and a different snapshot starts audible again. */}
      <Tooltip title={isMuted ? "Unmute the preview" : "Mute the preview"}>
        <IconButton size="small" onClick={onToggleMute}>
          {isMuted ? (
            <VolumeOffIcon fontSize="small" color="error" />
          ) : (
            <VolumeUpIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title="Restart the playable">
        <IconButton size="small" onClick={onReload}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* An export belongs to no folder, so this button is the only place its
          state can show; a folder's build marks its own row in the tree. */}
      <Tooltip
        title={
          exportJob && exportJob.state === "running"
            ? "Export running — you can leave this to it"
            : exportJob
              ? "Export finished — open to get the file"
              : "Export this snapshot, or a whole folder"
        }
      >
        <span className={classes.exportSlot}>
          <IconButton size="small" onClick={onExport} disabled={!editor.selectedFileName}>
            <GetAppIcon fontSize="small" />
          </IconButton>
          {exportJob && exportJob.state === "running" && (
            <CircularProgress size={12} thickness={6} className={classes.exportSpinner} />
          )}
          {exportJob && exportJob.state === "finished" && (
            <span
              className={`${classes.exportDot} ${
                exportJob.outcome === "error"
                  ? classes.exportDotError
                  : exportJob.outcome === "warning"
                    ? classes.exportDotWarning
                    : classes.exportDotSuccess
              }`}
            />
          )}
        </span>
      </Tooltip>

      <Tooltip title="Build this snapshot and get a link to share">
        <span>
          <IconButton size="small" onClick={onShareLink} disabled={!editor.selectedFileName}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}
